import qs from 'qs';
import { DialogPlugin, MessagePlugin } from 'tdesign-react';
import { debounce } from 'lodash';
import { getToken } from './utils/storage';
import eventBus from './utils/eventBus';
import { isVoid } from './utils/common';

const timeoutConfirm = debounce(() => {
  const dialog = DialogPlugin.alert({
    header: '登录超时',
    body: '登录超时，请重新登录',
    confirmBtn: {
      content: '重新登录',
    },
    closeBtn: false,
    onConfirm: () => {
      dialog.hide();
      setTimeout(() => {
        window.location.href = '/login';
      }, 400);
    },
  });
}, 300);

let msg;

const showError = debounce((errorMsg) => {
  if (msg) {
    MessagePlugin.close(msg);
    msg = null;
  }
  msg = MessagePlugin.error({
    content: (
      <>
        <strong style={{ color: '#5c5757' }}>{errorMsg.split(' : ')[0]}</strong> :{' '}
        {errorMsg.split(' : ')[1]}
      </>
    ),
    placement: 'top',
    offset: [0, 120],
  });
}, 100);

// fetch 模拟替代 axios
export async function client(endpoint, { body, ...customConfig } = {}) {
  const token = getToken();
  const config = {
    body,
    ...customConfig,
    headers: {
      ...customConfig.headers,
    },
  };

  if (!isVoid(token)) {
    config.headers['X-Handle-Admin-Token'] = token;
  }

  let data, response;
  try {
    response = await window.fetch(endpoint, config);
    // 埋点不关心返回
    if (endpoint === '/api/buried') {
      return;
    }
    if (config.isBlob) {
      return response.blob();
    } else {
      data = await response.json();
    }
    const { meta } = data;
    if (meta.code === 403) {
      timeoutConfirm();
      return;
    }
    if (response.ok && (meta.code === 31202 || meta.code === 31205)) {
      eventBus.emit('licenseMessage', meta);
      return;
    }
    if (response.ok && meta.code === 31212) {
      eventBus.emit('licenseMessage', meta);
      return;
    }

    // 以下code不作处理，由具体业务处理
    // 70101: 主集群创建的资源已在灾备集群存在
    // 70414: 绑定容灾集群时处理Topic资源报错
    if (response.ok && (meta.code === 200 || [70101, 70414].includes(meta.code))) {
      return {
        status: response.status,
        body: data,
        data: data.data, // 服务端data包装
        meta, // 服务端meta包装
        headers: response.headers,
        url: response.url,
      };
    }
    showError(meta.publicMessage);
    throw new Error(response.statusText);
  } catch (err) {
    if (response.status !== 200) {
      if (response.status === 502 || response.status === 504) {
        const { routes } = await import('./routes');
        routes.navigate('/error-502');
        return;
      }
      showError(`请求异常(${response.status})`);
    }
    return Promise.reject(err.message ? err.message : data);
  }
}

client.get = function (endpoint, params, customConfig = {}) {
  let _endpoint = endpoint;
  if (params) {
    _endpoint += qs.stringify(params, {
      addQueryPrefix: true,
    });
  }
  return client(_endpoint, { ...customConfig, method: 'GET' });
};

client.post = function (endpoint, body, customConfig = {}) {
  return client(endpoint, {
    body: JSON.stringify(body || {}),
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    ...customConfig,
  });
};

client.put = function (endpoint, body, customConfig = {}) {
  return client(endpoint, {
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
    method: 'PUT',
    ...customConfig,
  });
};

client.delete = function (endpoint, body, customConfig = {}) {
  let _endpoint = endpoint;
  if (body) {
    _endpoint += qs.stringify(body, {
      addQueryPrefix: true,
      arrayFormat: 'repeat',
    });
  }

  return client(_endpoint, {
    ...customConfig,
    method: 'DELETE',
  });
};

client.form = function (endpoint, body, customConfig = {}) {
  return client(endpoint, {
    body,
    method: 'POST',
    ...customConfig,
  });
};
